/**
 * Importe que rueda cuando cambia.
 *
 * Cuando entra una venta, la cifra del panel no salta: recorre el camino del
 * valor viejo al nuevo. Sirve para algo concreto y no solo para lucir — al
 * moverse, señala CUAL de las cifras cambio, que en un tablero con ocho numeros
 * es justo lo que uno busca.
 *
 * DETALLES QUE IMPORTAN
 * ---------------------
 * - Nunca anima el primer valor. Al abrir el panel, las cifras aparecen ya
 *   puestas; verlas contar desde cero cada vez que se entra seria un truco de
 *   demostracion, no una señal de que algo paso.
 * - Respeta `prefers-reduced-motion`. La regla global de CSS no alcanza aqui
 *   porque esto no es una animacion de CSS, asi que se consulta a mano.
 * - Formatea con `formatMoney`, la misma funcion que el resto del sistema: el
 *   numero intermedio tambien esta en centavos enteros y jamas se ve un
 *   redondeo raro a media transicion.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatMoney, formatMoneyShort } from '@abarrotes/shared';

const DURATION_MS = 620;

/** Salida suave: arranca rapido y frena al final. */
function easeOut(t: number): number {
  return 1 - (1 - t) ** 3;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function AnimatedAmount({
  cents,
  short = false,
  className,
}: {
  cents: number;
  /** Sin centavos, para las tarjetas de resumen. */
  short?: boolean;
  className?: string;
}): ReactNode {
  const [shown, setShown] = useState(cents);
  const previous = useRef(cents);
  const frame = useRef(0);

  useEffect(() => {
    const from = previous.current;
    previous.current = cents;

    if (from === cents) return;

    if (prefersReducedMotion()) {
      setShown(cents);
      return;
    }

    const start = performance.now();
    const delta = cents - from;

    const step = (t: number): void => {
      const progress = Math.min(1, (t - start) / DURATION_MS);
      // Se redondea a centavos enteros en cada cuadro: el valor intermedio es
      // tan valido como el final y nunca se ve una fraccion de centavo.
      setShown(Math.round(from + delta * easeOut(progress)));
      if (progress < 1) frame.current = requestAnimationFrame(step);
    };

    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [cents]);

  return (
    <span className={className}>{short ? formatMoneyShort(shown) : formatMoney(shown)}</span>
  );
}

/**
 * Conteo entero que rueda igual. Para tickets, piezas y demas cuentas.
 */
export function AnimatedCount({
  value,
  className,
}: {
  value: number;
  className?: string;
}): ReactNode {
  const [shown, setShown] = useState(value);
  const previous = useRef(value);
  const frame = useRef(0);

  useEffect(() => {
    const from = previous.current;
    previous.current = value;

    if (from === value) return;

    // Un salto de una unidad no gana nada con animarse; se ve como un parpadeo.
    if (prefersReducedMotion() || Math.abs(value - from) <= 1) {
      setShown(value);
      return;
    }

    const start = performance.now();
    const delta = value - from;

    const step = (t: number): void => {
      const progress = Math.min(1, (t - start) / DURATION_MS);
      setShown(Math.round(from + delta * easeOut(progress)));
      if (progress < 1) frame.current = requestAnimationFrame(step);
    };

    frame.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame.current);
  }, [value]);

  return <span className={className}>{shown}</span>;
}
